import React, { useState } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Select, Avatar } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { User } from '../../types';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

// 扩展User类型以适应表格需求
interface UserTableItem extends User {
  key: string;
  realName: string;
  lastLoginTime: string;
}

const UserList: React.FC = () => {
  const [loading, setLoading] = useState(false);

  // 模拟数据
  const mockData: UserTableItem[] = [
    {
      key: '1',
      id: 'USR001',
      name: 'admin',
      realName: '系统管理员',
      email: 'admin@example.com',
      phone: '13800138001',
      role: 'admin',
      status: 'active',
      avatar: '',
      createTime: '2024-01-01',
      lastLoginTime: '2024-01-15 14:30:00',
    },
    {
      key: '2',
      id: 'USR002',
      name: 'zhangsan',
      realName: '张三',
      email: 'zhangsan@example.com',
      phone: '13800138002',
      role: 'moderator',
      status: 'active',
      avatar: '',
      createTime: '2024-01-02',
      lastLoginTime: '2024-01-14 09:15:00',
    },
    {
      key: '3',
      id: 'USR003',
      name: 'lisi',
      realName: '李四',
      email: 'lisi@example.com',
      phone: '13800138003',
      role: 'user',
      status: 'active',
      avatar: '',
      createTime: '2024-01-03',
      lastLoginTime: '2024-01-13 16:45:00',
    },
    {
      key: '4',
      id: 'USR004',
      name: 'wangwu',
      realName: '王五',
      email: 'wangwu@example.com',
      phone: '13800138004',
      role: 'user',
      status: 'inactive',
      avatar: '',
      createTime: '2024-01-04',
      lastLoginTime: '2024-01-10 10:20:00',
    },
  ];

  const columns = [
    {
      title: '用户信息',
      key: 'userInfo',
      render: (record: UserTableItem) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.realName}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>@{record.name}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const roleMap: { [key: string]: { color: string } } = {
          'admin': { color: 'red' },
          'moderator': { color: 'blue' },
          'user': { color: 'default' },
        };
        const roleInfo = roleMap[role] || { color: 'default' };
        return <Tag color={roleInfo.color}>{role}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          active: { text: '正常', color: 'green' },
          inactive: { text: '停用', color: 'red' },
          locked: { text: '锁定', color: 'orange' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap];
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginTime',
      key: 'lastLoginTime',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: UserTableItem) => (
        <Space size="middle">
          <Button type="link" size="small">查看</Button>
          <Button type="link" size="small">编辑</Button>
          <Button type="link" size="small">重置密码</Button>
          {record.status === 'active' ? (
            <Button type="link" size="small" danger>停用</Button>
          ) : (
            <Button type="link" size="small">启用</Button>
          )}
          <Button type="link" size="small" danger>删除</Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    console.log('搜索:', value);
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>用户列表</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PlusOutlined />} type="primary">
                新增用户
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索用户名、真实姓名、邮箱"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select placeholder="用户角色" style={{ width: 120 }} allowClear>
                <Option value="admin">超级管理员</Option>
                <Option value="moderator">运营管理员</Option>
                <Option value="user">普通用户</Option>
              </Select>
              <Select placeholder="用户状态" style={{ width: 120 }} allowClear>
                <Option value="active">正常</Option>
                <Option value="inactive">停用</Option>
                <Option value="locked">锁定</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button>批量操作</Button>
              <Button>导出数据</Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={mockData}
          loading={loading}
          pagination={{
            total: mockData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>
    </div>
  );
};

export default UserList; 